/**
 * /operations/eos/deceased-workflow — M3X.EOS F.11.
 *
 * HR-facing deceased-employee workflow. Allows entering total
 * settlement amount + beneficiaries (name + kinship); service
 * computes per-beneficiary shares per Saudi inheritance law
 * (simplified). Bilingual condolence preview.
 *
 * Pack ref §1.7.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../components/ui";
import { api } from "../lib/api";

const KINSHIPS = [
  { value: "spouse_wife_with_children", label_en: "Wife (with children)", label_ar: "زوجة (مع أطفال)" },
  { value: "spouse_wife_no_children", label_en: "Wife (no children)", label_ar: "زوجة (بدون أطفال)" },
  { value: "spouse_husband_with_children", label_en: "Husband (with children)", label_ar: "زوج (مع أطفال)" },
  { value: "spouse_husband_no_children", label_en: "Husband (no children)", label_ar: "زوج (بدون أطفال)" },
  { value: "son", label_en: "Son", label_ar: "ابن" },
  { value: "daughter", label_en: "Daughter", label_ar: "ابنة" },
  { value: "father", label_en: "Father", label_ar: "أب" },
  { value: "mother", label_en: "Mother", label_ar: "أم" },
];

interface BeneInput {
  name_en: string;
  name_ar: string;
  kinship: string;
}

interface Allocation {
  beneficiary_id: string;
  name_en: string;
  name_ar: string;
  kinship: string;
  share_percent: string;
  share_amount: string;
  article_citation: string;
  confidence: string;
}

interface DeceasedSettlement {
  allocations: Allocation[];
  total_settlement: string;
  total_allocated: string;
  unallocated: string;
  currency: string;
  notes: string;
}

export function DeceasedWorkflowPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const [totalSettlement, setTotalSettlement] = useState("100000");
  const [benes, setBenes] = useState<BeneInput[]>([
    { name_en: "", name_ar: "", kinship: "spouse_wife_with_children" },
  ]);
  const [result, setResult] = useState<DeceasedSettlement | null>(null);
  const [deceasedNameEn, setDeceasedNameEn] = useState("");
  const [deceasedNameAr, setDeceasedNameAr] = useState("");
  const [condolence, setCondolence] = useState<{
    subject: string;
    body: string;
  } | null>(null);
  const [condolenceLocale, setCondolenceLocale] = useState<"en" | "ar">(
    isAr ? "ar" : "en",
  );

  async function allocate() {
    const beneficiaries = benes
      .filter((b) => b.name_en.trim() || b.name_ar.trim())
      .map((b, idx) => ({
        beneficiary_id: `bene_${idx + 1}`,
        ...b,
      }));
    const r = (await api.allocateDeceasedSettlement({
      total_settlement: totalSettlement,
      beneficiaries,
      currency: "SAR",
    })) as DeceasedSettlement;
    setResult(r);
  }

  async function renderCondolence() {
    const c = await api.renderDeceasedCondolence({
      deceased_name_en: deceasedNameEn || "Employee",
      deceased_name_ar: deceasedNameAr || "الموظف",
      legal_entity_name: "Riyadh Industries",
      locale: condolenceLocale,
    });
    setCondolence(c);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(
          "deceased.title",
          "Deceased Employee Workflow",
        )}
        subtitle={t(
          "deceased.subtitle",
          "HR uploads death certificate; AI classifies kinship; Rawatib computes inheritance shares (Saudi inheritance law). HR signs off disbursement.",
        )}
      />

      <InitiateDeceasedTerminationCard />


      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm text-amber-900">
          ✦{" "}
          {t(
            "deceased.note_najiz",
            "Najiz beneficiary-lookup integration is a v2 blocker (Saudi inheritance portal). For v1, beneficiaries are entered manually or via family-contact through Khidmat.",
          )}
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
        <h3 className="mb-3 text-base font-semibold">
          {t("deceased.settlement", "Total Settlement Amount")}
        </h3>
        <input
          type="number"
          value={totalSettlement}
          onChange={(e) => setTotalSettlement(e.target.value)}
          className="w-full rounded-md border border-border-default p-2 text-sm"
        />
      </div>

      <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold">
            {t("deceased.beneficiaries", "Beneficiaries")}
          </h3>
          <button
            type="button"
            onClick={() =>
              setBenes([
                ...benes,
                { name_en: "", name_ar: "", kinship: "son" },
              ])
            }
            className="rounded border border-brand-blue px-2 py-1 text-xs text-brand-blue"
          >
            +{" "}
            {t("deceased.add_beneficiary", "Add beneficiary")}
          </button>
        </div>
        {benes.map((b, idx) => (
          <div
            key={idx}
            className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-4"
          >
            <input
              placeholder="Name (EN)"
              value={b.name_en}
              onChange={(e) => {
                const c = [...benes];
                c[idx] = { ...c[idx], name_en: e.target.value };
                setBenes(c);
              }}
              className="rounded-md border border-border-default p-2 text-sm"
            />
            <input
              placeholder="الاسم (AR)"
              value={b.name_ar}
              onChange={(e) => {
                const c = [...benes];
                c[idx] = { ...c[idx], name_ar: e.target.value };
                setBenes(c);
              }}
              className="rounded-md border border-border-default p-2 text-sm"
              dir="rtl"
            />
            <select
              value={b.kinship}
              onChange={(e) => {
                const c = [...benes];
                c[idx] = { ...c[idx], kinship: e.target.value };
                setBenes(c);
              }}
              className="rounded-md border border-border-default p-2 text-sm"
            >
              {KINSHIPS.map((k) => (
                <option key={k.value} value={k.value}>
                  {isAr ? k.label_ar : k.label_en}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setBenes(benes.filter((_, i) => i !== idx))}
              className="rounded border border-rose-300 text-xs text-rose-700"
            >
              {t("deceased.remove", "Remove")}
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={allocate}
          className="mt-3 rounded-md bg-brand-blue px-3 py-1.5 text-sm font-medium text-white"
        >
          {t(
            "deceased.compute_shares",
            "Compute inheritance shares (Rawatib)",
          )}
        </button>
      </div>

      {result && (
        <div className="rounded-lg border border-brand-blue/30 bg-brand-blue/5 p-5">
          <h3 className="mb-3 text-base font-semibold text-brand-deep">
            {t("deceased.allocations_title", "Per-Beneficiary Allocation")}
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-blue/20 text-text-tertiary">
                <th className="py-2 text-start text-xs uppercase">
                  {t("deceased.beneficiary", "Beneficiary")}
                </th>
                <th className="py-2 text-start text-xs uppercase">
                  {t("deceased.kinship", "Kinship")}
                </th>
                <th className="py-2 text-end text-xs uppercase">
                  {t("deceased.share_percent", "Share %")}
                </th>
                <th className="py-2 text-end text-xs uppercase">
                  {t("deceased.share_amount", "Amount")}
                </th>
              </tr>
            </thead>
            <tbody>
              {result.allocations.map((a, idx) => (
                <tr key={idx} className="border-b border-brand-blue/10">
                  <td className="py-2">{isAr ? a.name_ar : a.name_en}</td>
                  <td className="py-2 font-mono text-xs">{a.kinship}</td>
                  <td className="py-2 text-end font-mono">{a.share_percent}%</td>
                  <td className="py-2 text-end font-mono">{a.share_amount}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-brand-blue bg-brand-blue/10 font-bold">
                <td className="py-2" colSpan={2}>
                  {t("deceased.total_allocated", "Allocated")}
                </td>
                <td className="py-2 text-end">—</td>
                <td className="py-2 text-end font-mono">
                  {result.total_allocated} {result.currency}
                </td>
              </tr>
              {Number(result.unallocated) > 0 && (
                <tr className="bg-amber-50 text-amber-900">
                  <td className="py-2" colSpan={2}>
                    {t(
                      "deceased.unallocated",
                      "Unallocated (escrow / Sharia advisor review)",
                    )}
                  </td>
                  <td className="py-2 text-end">—</td>
                  <td className="py-2 text-end font-mono">
                    {result.unallocated}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-text-secondary">{result.notes}</p>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
        <h3 className="mb-3 text-base font-semibold">
          {t(
            "deceased.condolence_title",
            "Khidmat Condolence + Settlement Info Message",
          )}
        </h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <input
            placeholder={t("deceased.deceased_name_en", "Deceased name (EN)") as string}
            value={deceasedNameEn}
            onChange={(e) => setDeceasedNameEn(e.target.value)}
            className="rounded-md border border-border-default p-2 text-sm"
          />
          <input
            placeholder={t("deceased.deceased_name_ar", "اسم المتوفى (AR)") as string}
            value={deceasedNameAr}
            onChange={(e) => setDeceasedNameAr(e.target.value)}
            className="rounded-md border border-border-default p-2 text-sm"
            dir="rtl"
          />
          <select
            value={condolenceLocale}
            onChange={(e) => setCondolenceLocale(e.target.value as "en" | "ar")}
            className="rounded-md border border-border-default p-2 text-sm"
          >
            <option value="en">English</option>
            <option value="ar">العربية</option>
          </select>
        </div>
        <button
          type="button"
          onClick={renderCondolence}
          className="mt-3 rounded-md border border-brand-purple px-3 py-1.5 text-xs text-brand-purple"
        >
          ✦{" "}
          {t("deceased.render_condolence", "Render condolence (Khidmat)")}
        </button>
        {condolence && (
          <div className="mt-3 rounded-md border border-brand-purple/30 bg-brand-purple/5 p-4">
            <h4 className="text-sm font-semibold text-brand-purple">
              {condolence.subject}
            </h4>
            <pre
              dir={condolenceLocale === "ar" ? "rtl" : "ltr"}
              className="mt-2 whitespace-pre-wrap text-xs text-text-secondary"
            >
              {condolence.body}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function InitiateDeceasedTerminationCard() {
  const { t } = useTranslation();
  const [employeeId, setEmployeeId] = useState("");
  const [legalEntityId, setLegalEntityId] = useState("");
  const [dateOfDeath, setDateOfDeath] = useState("");
  const [certUrl, setCertUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function fire() {
    if (!employeeId || !legalEntityId || !dateOfDeath) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = (await api.initiateDeceasedTermination({
        employee_id: employeeId,
        legal_entity_id: legalEntityId,
        date_of_death: dateOfDeath,
        death_certificate_url: certUrl || null,
      })) as { id: string; workflow_state: string };
      setResult(
        `M18 termination ${r.id} opened in state '${r.workflow_state}'. EOS auto-computed with Article 113 uplift.`,
      );
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-brand-purple/30 bg-brand-purple/5 p-5">
      <h3 className="mb-3 text-base font-semibold text-brand-purple">
        ✦{" "}
        {t(
          "deceased.initiate_title",
          "Wire deceased trigger into M18 termination flow",
        )}
      </h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <input
          placeholder="Employee UUID"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          className="rounded-md border border-border-default p-2 text-sm"
        />
        <input
          placeholder="Legal Entity UUID"
          value={legalEntityId}
          onChange={(e) => setLegalEntityId(e.target.value)}
          className="rounded-md border border-border-default p-2 text-sm"
        />
        <input
          type="date"
          value={dateOfDeath}
          onChange={(e) => setDateOfDeath(e.target.value)}
          className="rounded-md border border-border-default p-2 text-sm"
        />
        <input
          placeholder="Certificate URL (optional)"
          value={certUrl}
          onChange={(e) => setCertUrl(e.target.value)}
          className="rounded-md border border-border-default p-2 text-sm"
        />
      </div>
      <button
        type="button"
        onClick={fire}
        disabled={busy || !employeeId || !legalEntityId || !dateOfDeath}
        className="mt-3 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
      >
        {busy
          ? t("deceased.firing", "Opening M18 workflow…")
          : t(
              "deceased.initiate_btn",
              "Open M18 deceased termination",
            )}
      </button>
      {result && (
        <p className="mt-2 rounded bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900">
          ✓ {result}
        </p>
      )}
      {err && (
        <p className="mt-2 rounded bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
          {err}
        </p>
      )}
    </div>
  );
}

export default DeceasedWorkflowPage;
